// pages/admin/InvoiceManagement.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import {
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
} from "../components/AdminPageShell";
import { useAdminMarketplace, useAdminPayments } from "../store/admin";
import { useConfirmDialog } from "../components/ConfirmDialogProvider";

import InvoiceSummaryCards from "./components/invoices/InvoiceSummaryCards";
import InvoiceFilters from "./components/invoices/InvoiceFilters";
import InvoiceTable from "./components/invoices/InvoiceTable";
import InvoicePagination from "./components/invoices/InvoicePagination";
import { formatAmountForDisplay } from "./components/invoices/invoiceUtils";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap-icons/font/bootstrap-icons.css";

const PAGE_SIZE = 20;

/** Keep only purchases inside the selected rolling window. */
function filterByTime(purchases, timeFilter) {
  if (!timeFilter || timeFilter === "all") return purchases;
  const now = new Date();
  const start = new Date(now);
  switch (timeFilter) {
    case "daily":
      start.setHours(0, 0, 0, 0);
      break;
    case "weekly":
      start.setDate(now.getDate() - 7);
      break;
    case "monthly":
      start.setMonth(now.getMonth() - 1);
      break;
    case "yearly":
      start.setFullYear(now.getFullYear() - 1);
      break;
    default:
      return purchases;
  }
  return purchases.filter((purchase) => {
    const created = new Date(purchase.createdAt);
    return !Number.isNaN(created.getTime()) && created >= start;
  });
}

/** Derive summary cards from the purchases currently loaded. */
function buildSummary(purchases) {
  const completed = purchases.filter((p) => p.status === "COMPLETED");
  const pending = purchases.filter((p) => p.status === "PENDING" || p.status === "PROCESSING");
  const failed = purchases.filter((p) => p.status === "FAILED" || p.status === "CANCELLED");
  const sum = (rows) => rows.reduce((total, row) => total + (Number(row.amount) || 0), 0);

  return {
    totalInvoices: purchases.length,
    totalRevenue: formatAmountForDisplay(sum(completed)),
    totalSuccessfulInvoices: completed.length,
    totalPendingInvoices: pending.length,
    totalFailedInvoices: failed.length,
    totalPendingAmount: formatAmountForDisplay(sum(pending)),
    totalFailedAmount: formatAmountForDisplay(sum(failed)),
  };
}

// --- Invoice / Revenue Management Component ---
const InvoiceManagement = () => {
  const confirm = useConfirmDialog();
  const [isSuccess, setIsSuccess] = useState(true);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [openTimeDropdown, setOpenTimeDropdown] = useState(null);

  const {
    marketplacePurchases: purchases,
    marketplacePurchasesLoading,
    marketplacePurchasesError,
    marketplacePurchasesTotal: totalPurchases,
    marketplacePurchasesPages: totalPages,
    fetchMarketplacePurchases,
    revokeMarketplacePurchase,
    restoreMarketplacePurchase,
  } = useAdminMarketplace();

  const { adminVerifyPayment } = useAdminPayments();

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const loadPurchases = useCallback(async () => {
    const result = await fetchMarketplacePurchases({
      page,
      limit: PAGE_SIZE,
      status: statusFilter === "all" ? undefined : statusFilter,
      search: search || undefined,
    });
    if (!result.success) {
      showToast(result.error || "Failed to load purchases", false);
    }
  }, [fetchMarketplacePurchases, page, search, statusFilter, showToast]);

  useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  const visiblePurchases = useMemo(
    () => filterByTime(Array.isArray(purchases) ? purchases : [], timeFilter),
    [purchases, timeFilter]
  );

  const summary = useMemo(() => buildSummary(visiblePurchases), [visiblePurchases]);

  const handleSearchSubmit = () => {
    setPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleRevoke = async (invoice) => {
    const reason = window.prompt(
      `Reason for revoking access to ${invoice.reference}?`,
      invoice.revokedReason || ""
    );
    if (reason === null) return;

    return confirm({
      title: "Revoke purchase access?",
      description: "The learner will lose access to this purchase immediately.",
      confirmLabel: "Revoke access",
      onConfirm: async () => {
        const result = await revokeMarketplacePurchase(invoice.id, reason.trim());
        if (!result.success) throw new Error(result.error || "Failed to revoke access.");
        showToast("Purchase access revoked", true);
        await loadPurchases();
      },
    });
  };

  const handleRestore = async (invoice) => {
    return confirm({
      title: "Restore purchase access?",
      description: "The learner will regain access to this purchase.",
      confirmLabel: "Restore access",
      onConfirm: async () => {
        const result = await restoreMarketplacePurchase(invoice.id);
        if (!result.success) throw new Error(result.error || "Failed to restore access.");
        showToast("Purchase access restored", true);
        await loadPurchases();
      },
    });
  };

  const handleVerify = async (invoice, force) => {
    return confirm({
      title: force ? "Force-complete this payment?" : "Verify this payment with Paystack?",
      description: force
        ? "This completes the transaction without checking the gateway."
        : "The gateway must report the payment as successful.",
      confirmLabel: force ? "Force verify" : "Verify",
      onConfirm: async () => {
        const result = await adminVerifyPayment(invoice.reference, { force });
        if (!result.success) throw new Error(result.error || "Failed to verify payment.");
        showToast("Payment verified", true);
        await loadPurchases();
      },
    });
  };

  return (
    <div>
      <AdminNavBar title="Revenue Management" context="Manage marketplace purchases and access." />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        <button
          className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:bg-slate-50 md:right-8"
          onClick={() => {
            const subBody = document.querySelector(".sub-body");
            if (subBody) subBody.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <FontAwesomeIcon icon={faAngleUp} />
        </button>

        <AdminSectionHeader
          title="Marketplace Purchases"
          description="Review marketplace purchases and manage learner access."
          count={`${totalPurchases} purchases`}
        />

        <InvoiceSummaryCards summary={summary} timeFilter={timeFilter} />

        <InvoiceFilters
          search={search}
          onSearchChange={setSearch}
          onSearchSubmit={handleSearchSubmit}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          timeFilter={timeFilter}
          onTimeFilterChange={(value) => setTimeFilter(value)}
          openTimeDropdown={openTimeDropdown}
          onToggleTimeDropdown={() =>
            setOpenTimeDropdown(openTimeDropdown === null ? "actions" : null)
          }
        />

        {marketplacePurchasesError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {marketplacePurchasesError}
          </div>
        )}

        <AdminTablePanel>
          <div className="overflow-x-auto">
            <InvoiceTable
              invoices={visiblePurchases}
              isReloading={marketplacePurchasesLoading}
              onRevoke={handleRevoke}
              onRestore={handleRestore}
              onVerify={handleVerify}
            />
          </div>
        </AdminTablePanel>

        {!marketplacePurchasesLoading && totalPages > 1 && (
          <InvoicePagination page={page} lastPage={totalPages} onPageChange={setPage} />
        )}
      </AdminPageBody>
    </div>
  );
};

export default InvoiceManagement;
